'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useScreenSaverTimer } from '@/hooks/useScreenSaverTimer';

import { useGlobalConfig } from '@/components/GlobalContext';

import { useConfig } from '@/appconfig/ConfigContext';
import { AppResolution } from '@/enums';
import { useMediaPostersByContentUrls } from '@/services/graphQL';
import { Language } from '@/services/graphQL/api/generated/types_web';
import { parseMediaIdFromContentUrl } from '@/utils/contentUrl';
import { getLocalization } from '@/utils/localization';
import { normalizeDeviceLanguage } from '@/utils/normalizeDeviceLanguage';
import { POSTER_PLACEHOLDER } from '@/utils/placeholders';

const DEFAULT_INACTIVITY_MINUTES = 15;
const DEFAULT_SLIDE_INTERVAL_SECONDS = 10;
const FADE_DURATION_MS = 300;

const SCREENSAVER_BG_IMAGE =
  'url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOTIwIiBoZWlnaHQ9IjEwODAiIGZpbGw9Im5vbmUiIHZpZXdCb3g9IjAgMCAxOTIwIDEwODAiPjxwYXRoIGZpbGw9IiNmNWY1ZjUiIGQ9Ik0wIDBoMTkyMHYxMDgwSDB6Ii8+PGcgY2xpcC1wYXRoPSJ1cmwoI2EpIj48bWFzayBpZD0iYiIgd2lkdGg9IjE5MjAiIGhlaWdodD0iMTA4MCIgeD0iMCIgeT0iMCIgbWFza1VuaXRzPSJ1c2VyU3BhY2VPblVzZSIgc3R5bGU9Im1hc2stdHlwZTpsdW1pbmFuY2UiPjxwYXRoIGZpbGw9IiNmZmYiIGQ9Ik0xOTIwIDBIMHYxMDgwaDE5MjB6Ii8+PC9tYXNrPjxnIG1hc2s9InVybCgjYikiPjxwYXRoIGZpbGw9InVybCgjYykiIGQ9Ik0xOTIwIDBIMHYxMDgwaDE5MjB6Ii8+PHBhdGggZmlsbD0idXJsKCNkKSIgZmlsbC1vcGFjaXR5PSIuOCIgZD0iTTE5MjYgMEgwdjExNTJoMTkyNnoiLz48cGF0aCBmaWxsPSJ1cmwoI2UpIiBkPSJNMTkyMC0zOEgtNnYxMTUyaDE5MjZ6Ii8+PC9nPjwvZz48ZGVmcz48cmFkaWFsR3JhZGllbnQgaWQ9ImQiIGN4PSIwIiBjeT0iMCIgcj0iMSIgZ3JhZGllbnRUcmFuc2Zvcm09Im1hdHJpeCgwIDEwMzUuMTIgLTE5NDEuMTEgMCA5NjMgLTI1NikiIGdyYWRpZW50VW5pdHM9InVzZXJTcGFjZU9uVXNlIj48c3RvcCBvZmZzZXQ9Ii40MzQxMDIiIHN0b3AtY29sb3I9IiMwZDBlN2YiIHN0b3Atb3BhY2l0eT0iLjYiLz48c3RvcCBvZmZzZXQ9Ii42MDAxMDEiIHN0b3AtY29sb3I9IiMwODBiNGUiIHN0b3Atb3BhY2l0eT0iLjYiLz48c3RvcCBvZmZzZXQ9Ii44NjQzNDgiIHN0b3AtY29sb3I9IiMwNDA3MjAiIHN0b3Atb3BhY2l0eT0iLjYiLz48L3JhZGlhbEdyYWRpZW50PjxyYWRpYWxHcmFkaWVudCBpZD0iZSIgY3g9IjAiIGN5PSIwIiByPSIxIiBncmFkaWVudFRyYW5zZm9ybT0ibWF0cml4KDAgMTI5Ny42IC0zMTM0LjkgMCA5NTcgLTE4My42KSIgZ3JhZGllbnRVbml0cz0idXNlclNwYWNlT25Vc2UiPjxzdG9wIG9mZnNldD0iLjA3NDEyNTIiIHN0b3AtY29sb3I9IiMwMGE5ZWUiLz48c3RvcCBvZmZzZXQ9Ii40NDYwMDgiIHN0b3AtY29sb3I9IiMwZDBlN2YiIHN0b3Atb3BhY2l0eT0iMCIvPjwvcmFkaWFsR3JhZGllbnQ+PGxpbmVhckdyYWRpZW50IGlkPSJjIiB4MT0iOTYwIiB4Mj0iOTYwIiB5MT0iLTE5OC4yODEiIHkyPSIxMDgwIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHN0b3Agc3RvcC1jb2xvcj0iIzAwMDgzZiIvPjxzdG9wIG9mZnNldD0iLjU5NTcxMiIgc3RvcC1jb2xvcj0iIzAzMDcyMCIvPjwvbGluZWFyR3JhZGllbnQ+PGNsaXBQYXRoIGlkPSJhIj48cGF0aCBmaWxsPSIjZmZmIiBkPSJNMCAwaDE5MjB2MTA4MEgweiIvPjwvY2xpcFBhdGg+PC9kZWZzPjwvc3ZnPg==")';

type Slide = { posters: string[] };

type LangKey = Lowercase<`${Language}`>;

type ScreenSaverConfig = {
  inactivityMinutes?: number;
  slideIntervalSeconds?: number;
  slides?: Partial<Record<LangKey, Slide[]>>;
};

type ScreenSaverOverlayProps = {
  slides: Slide[];
  slideIntervalMs: number;
  langKey: LangKey;
  is720p: boolean;
};

// to override screen saver inactivity time at development or qa time.
declare global {
  interface Window {
    inactivityMinutes?: number;
  }
}

/**
 * Renders the actual screensaver overlay with config-based poster images.
 * Only mounted while the screensaver is visible so all state and preloaded
 * Image objects are released to GC as soon as the screensaver is dismissed.
 */
const ScreenSaverOverlay: React.FC<ScreenSaverOverlayProps> = ({
  slides,
  slideIntervalMs,
  langKey,
  is720p,
}) => {
  // Scale all pixel values for 720p devices (root font-size is 10.666px instead of 16px).
  const scale = is720p ? 720 / 1080 : 1;
  const px = (val: number) => Math.round(val * scale);

  // Current slide index — ref drives the interval closure; state triggers render.
  const slideIndexRef = useRef(0);
  const [slideIndex, setSlideIndex] = useState(0);

  // Two-phase opacity crossfade without an animation library.
  const [isFading, setIsFading] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fade-in on mount: start transparent, become opaque on the next frame.
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setIsMounted(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  // Track which slide indices have all their images ready in the cache.
  // A ref is used so the interval callback always sees the latest value without
  // a stale closure; we don't need a re-render when later slides become ready.
  const loadedSlidesRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!slides.length) return;
    loadedSlidesRef.current = new Set();
    const allImages: HTMLImageElement[] = [];
    for (let slideIdx = 0; slideIdx < slides.length; slideIdx++) {
      const slide = slides[slideIdx];
      const total = slide.posters.length;
      if (total === 0) {
        loadedSlidesRef.current.add(slideIdx);
        continue;
      }
      let settled = 0;
      const onSettle = () => {
        settled += 1;
        if (settled === total) loadedSlidesRef.current.add(slideIdx);
      };
      for (let i = 0; i < slide.posters.length; i++) {
        const img = new window.Image();
        img.onload = img.onerror = onSettle;
        img.src = slide.posters[i];
        allImages.push(img);
      }
    }
    return () => {
      for (let i = 0; i < allImages.length; i++) {
        allImages[i].onload = null;
        allImages[i].onerror = null;
      }
    };
  }, [slides]);

  // Auto-advance. The `loadedSlidesRef` check ensures we never advance to a
  // slide whose images haven't settled yet.
  useEffect(() => {
    if (slides.length <= 1) return;
    slideIndexRef.current = 0;
    setSlideIndex(0);
    setIsFading(false);
    const interval = setInterval(() => {
      const next = (slideIndexRef.current + 1) % slides.length;
      if (!loadedSlidesRef.current.has(next)) return;
      setIsFading(true);
      fadeTimerRef.current = setTimeout(() => {
        slideIndexRef.current = next;
        setSlideIndex(next);
        setIsFading(false);
      }, FADE_DURATION_MS);
    }, slideIntervalMs);
    return () => {
      clearInterval(interval);
      if (fadeTimerRef.current !== null) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [slides.length, slideIntervalMs]);

  if (!slides.length) return null;

  // Layout dimensions (1080p base, scaled for 720p).
  const posterLeft = px(233);
  const posterWidth = px(421);
  const posterHeight = px(632);
  const posterGap = px(96);
  const posterBorderRadius = px(24);
  const lineHeightPx = px(96);
  const logoWidth = px(323);
  const logoHeight = px(57);
  // Gap between the tagline text and the Crave logo (from design).
  const textLogoGap = px(32);
  // Gap from poster bottom to top of the tagline text (from design).
  const posterToTextGap = px(56);

  // Position the full content block per the new design (1080p):
  // Block height: 632 + 56 + 96 + 32 + 57 = 873px.
  // Top margin: 127px, bottom margin: 80px (logo bottom at 1000px).
  const posterTop = px(127);
  const textTop = posterTop + posterHeight + posterToTextGap;
  const logoTop = textTop + lineHeightPx + textLogoGap;

  const fontSize = px(48);

  const text = getLocalization(langKey).screensaver.tagline;

  return (
    // Xi6 compatibility: top-0 left-0 w-full h-full instead of inset-0
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Screensaver"
      className="fixed top-0 left-0 w-full h-full z-[9999]"
      style={{
        opacity: isMounted ? 1 : 0,
        transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
        backgroundImage: SCREENSAVER_BG_IMAGE,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Crave logo — centered below the tagline text */}
      <div
        className="absolute"
        style={{
          top: logoTop,
          left: '50%',
          transform: 'translateX(-50%)',
          width: logoWidth,
          height: logoHeight,
        }}
      >
        <img
          src="/images/crave-logo.svg"
          alt="Crave"
          style={{ width: logoWidth, height: logoHeight }}
        />
      </div>

      {/* Poster rows — ALL slides are kept in the DOM simultaneously.
          Only the active slide is visible (opacity > 0). This means the <img>
          elements for every slide are created ONCE and their src never changes,
          so the browser never issues a second network request for any image.
          key={i} (poster index) is used instead of key={src} to avoid duplicate
          key warnings when multiple posters fall back to the same placeholder URL. */}
      {slides.map((slide, si) => (
        <div
          key={si}
          data-testid={`screensaver-slide-${si}`}
          className="absolute flex"
          style={{
            top: posterTop,
            left: posterLeft,
            opacity: si === slideIndex ? (isFading ? 0 : 1) : 0,
            transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
            pointerEvents: 'none',
          }}
        >
          {slide.posters.map((src, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div style={{ width: posterGap, flexShrink: 0 }} />}
              <div
                style={{
                  width: posterWidth,
                  height: posterHeight,
                  borderRadius: posterBorderRadius,
                  overflow: 'hidden',
                  flexShrink: 0,
                }}
              >
                <img
                  src={src}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = POSTER_PLACEHOLDER;
                  }}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      ))}

      {/* Tagline text below posters */}
      <div
        className="absolute text-center"
        style={{
          top: textTop,
          left: posterLeft,
          right: posterLeft,
          fontSize,
          lineHeight: `${lineHeightPx}px`,
          fontWeight: 700,
          fontFamily: '"Proxima Nova", sans-serif',
          color: '#FFF',
        }}
      >
        {text}
      </div>
    </div>
  );
};

/**
 * Outer shell — only holds the inactivity timer and config reads (both
 * lightweight). When the screensaver is not visible, nothing is rendered and
 * ScreenSaverOverlay is unmounted, releasing all its state and preloaded
 * Image objects to the garbage collector.
 */
const ScreenSaver: React.FC = () => {
  const config = useConfig();
  const { userLanguage, userMaturity, currentResolution } = useGlobalConfig();

  const screensaverCfg = config?.screensaver as ScreenSaverConfig | undefined;

  // Read inactivity minutes from config, defaulting to DEFAULT_INACTIVITY_MINUTES
  // it could be overridden by window.inactivityMinutes for testing purposes
  const inactivityMinutes =
    typeof window !== 'undefined' && window.inactivityMinutes !== undefined
      ? Number(window.inactivityMinutes)
      : (screensaverCfg?.inactivityMinutes ?? DEFAULT_INACTIVITY_MINUTES);

  const inactivityMs = inactivityMinutes * 60 * 1000;

  const slideIntervalMs =
    Number(screensaverCfg?.slideIntervalSeconds ?? DEFAULT_SLIDE_INTERVAL_SECONDS) * 1000;

  const { isVisible } = useScreenSaverTimer(inactivityMs);

  // French if either the app language setting or the device locale is French; English
  // otherwise.
  const langKey = useMemo<LangKey>(() => {
    if (userLanguage === Language.Fr) return 'fr';
    if (
      typeof navigator !== 'undefined' &&
      normalizeDeviceLanguage(navigator?.language) === Language.Fr
    )
      return 'fr';
    return 'en';
  }, [userLanguage]);

  const rawSlides = useMemo<Slide[]>(
    () => screensaverCfg?.slides?.[langKey] ?? [],
    [screensaverCfg, langKey]
  );

  // Collect all unique content URLs across all slides for the active language.
  const allContentUrls = useMemo(
    () => [...new Set(rawSlides.flatMap((s) => s.posters))],
    [rawSlides]
  );

  // Only fetch poster images when the screensaver is about to show (isVisible = true).
  // Passing [] while hidden keeps the hook in its idle/reset state so no network
  // request fires during navigation events (e.g. profile selection → home redirect)
  // that would cancel the in-flight query before it resolves.
  const { loading: postersLoading, items: resolvedItems } = useMediaPostersByContentUrls(
    { userLanguage, userMaturity },
    isVisible ? allContentUrls : []
  );

  // Build a map: content URL → resolved CDN poster URL.
  // item.id from the API may not exactly equal the numeric string we parsed
  // (e.g. "media/45159" vs "45159"), so we do a suffix match as a fallback.
  const posterUrlMap = useMemo(() => {
    const map = new Map<string, string>();
    for (let ci = 0; ci < allContentUrls.length; ci++) {
      const contentUrl = allContentUrls[ci];
      const parsedId = parseMediaIdFromContentUrl(contentUrl);
      if (!parsedId) continue;
      let imageUrl: string | undefined;
      for (let ri = 0; ri < resolvedItems.length; ri++) {
        const it = resolvedItems[ri];
        if (
          it.id === parsedId ||
          it.id.endsWith(`/${parsedId}`) ||
          it.id.endsWith(`-${parsedId}`)
        ) {
          imageUrl = it.metadataImages?.poster?.url;
          break;
        }
      }
      if (imageUrl) map.set(contentUrl, imageUrl);
    }
    return map;
  }, [allContentUrls, resolvedItems]);

  const is720p = currentResolution === AppResolution.Hd;

  // Swap content URLs for real CDN image URLs once the query has finished.
  // Falls back to POSTER_PLACEHOLDER so every slot stays filled.
  // Appends ?width=N&maintain_aspect=1 to CDN URLs to fetch a smaller image
  // sized to the actual rendered poster width (421px @ 1080p, 281px @ 720p).
  const slides: Slide[] = useMemo(() => {
    const targetWidth = is720p ? 281 : 421;
    return rawSlides.map((slide) => ({
      posters: slide.posters.map((url) => {
        const imageUrl = posterUrlMap.get(url) ?? POSTER_PLACEHOLDER;
        return imageUrl.startsWith('http')
          ? `${imageUrl}?width=${targetWidth}&maintain_aspect=1`
          : imageUrl;
      }),
    }));
  }, [rawSlides, posterUrlMap, is720p]);

  // Only render the overlay once the initial poster query has settled so we
  // never show placeholder images. When visible, ScreenSaverOverlay handles
  // its own per-slide preloading and releases everything on unmount.
  return isVisible && !postersLoading ? (
    <ScreenSaverOverlay
      slides={slides}
      slideIntervalMs={slideIntervalMs}
      langKey={langKey}
      is720p={is720p}
    />
  ) : null;
};

export default ScreenSaver;
